import type { OutlineEntry, ReportBlock } from '@/lib/types/report'
import { LIVE_STATUSES } from './liveStatus'

/** Interleave pending-section ghosts into the block list at their profile position.
 *  Only live reports get ghosts — complete/tables_failed render the blocks untouched.
 *  Unwritten outline entries never have a block, so each ghost slots in exactly once. */
export function mergeOutline(
  blocks: ReportBlock[],
  outline: OutlineEntry[],
  status: string,
): ReportBlock[] {
  if (outline.length === 0 || !LIVE_STATUSES.has(status)) return blocks
  const outlineIndex = new Map(outline.map((o, i) => [o.id, i]))
  const ghost = (o: OutlineEntry): ReportBlock => ({
    type: 'pending_section',
    id: o.id,
    title: o.title,
    data: {},
  })
  const merged: ReportBlock[] = []
  let next = 0 // first outline entry not yet emitted (as block or ghost)
  for (const b of blocks) {
    const idx = b.id != null ? outlineIndex.get(b.id) : undefined
    if (idx !== undefined) {
      for (; next < idx; next++) {
        if (!outline[next].written) merged.push(ghost(outline[next]))
      }
      next = idx + 1
    }
    merged.push(b)
  }
  for (; next < outline.length; next++) {
    if (!outline[next].written) merged.push(ghost(outline[next]))
  }
  return merged
}

/** Section progress for the live header — counts every profile section. */
export function outlineProgress(outline: OutlineEntry[]): { written: number; total: number } {
  return { written: outline.filter((o) => o.written).length, total: outline.length }
}
